import { useState, useCallback, useEffect } from 'react';
import type { SessionInfo } from '../types/gateway';
import { buildSessionCreate, buildSessionList } from '../gateway/protocol';

interface UseSessionsProps {
  send: (data: unknown) => void;
  onMessage: (handler: (data: unknown) => void) => () => void;
}

export function useSessions({ send, onMessage }: UseSessionsProps) {
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  useEffect(() => {
    const unsub = onMessage((data: unknown) => {
      const msg = data as { type?: string; data?: unknown };
      if (msg.type === 'session.list') {
        const payload = msg.data as { sessions?: SessionInfo[] };
        if (payload?.sessions) {
          setSessions(payload.sessions);
          if (!activeSessionId && payload.sessions.length > 0) {
            setActiveSessionId(payload.sessions[0].id);
          }
        }
      } else if (msg.type === 'session.created') {
        const session = msg.data as SessionInfo;
        setSessions((prev) => [session, ...prev]);
        setActiveSessionId(session.id);
      }
    });

    // Request session list on mount
    send(buildSessionList());

    return unsub;
  }, [send, onMessage, activeSessionId]);

  const createSession = useCallback(
    (name?: string) => {
      send(buildSessionCreate(name));
    },
    [send]
  );

  const switchSession = useCallback((id: string) => {
    setActiveSessionId(id);
  }, []);

  return {
    sessions,
    activeSessionId,
    createSession,
    switchSession,
  };
}
